import {
  EDGE_API,
  EDGE_API_FALLBACK,
  commandAppUrl,
  MARKETING_ORIGIN,
} from "./hosts";

/** starter = free/self-host; team/hosted = paid OG Hosted; scale = enterprise */
export type PlanTier = "starter" | "team" | "hosted" | "scale";

const CHECKOUT_INTENT_KEY = "og_checkout_intent";

/** Remember "wants Hosted" across sign-up → create-org → Command. */
export function setCheckoutIntent(tier: "team" = "team"): void {
  try {
    sessionStorage.setItem(CHECKOUT_INTENT_KEY, tier);
  } catch {
    /* private mode */
  }
}

export function peekCheckoutIntent(): "team" | null {
  try {
    const v = sessionStorage.getItem(CHECKOUT_INTENT_KEY);
    return v === "team" ? "team" : null;
  } catch {
    return null;
  }
}

export function clearCheckoutIntent(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
  } catch {
    /* ok */
  }
}

/** Prefer custom API host; fall back to workers.dev if DNS flaky. */
async function edgeFetch(
  path: string,
  init?: RequestInit
): Promise<Response> {
  const bases = [EDGE_API, EDGE_API_FALLBACK].filter(
    (b, i, a) => b && a.indexOf(b) === i
  );
  let lastErr: unknown;
  for (const base of bases) {
    try {
      const r = await fetch(`${base.replace(/\/$/, "")}${path}`, init);
      // Prefer a reachable edge even if 4xx business error
      return r;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error("edge_unreachable");
}

/** Stripe Checkout for OG Hosted ($9.99/mo). Requires clerkOrgId. */
export async function startTeamCheckout(opts: {
  customerEmail?: string | null;
  clientReferenceId?: string | null;
  clerkOrgId?: string | null;
}): Promise<{ url: string }> {
  if (!opts.clerkOrgId) {
    // Random users must have an org so entitlement lands on the workspace
    setCheckoutIntent("team");
    throw new Error(
      "Create your organization first — then subscribe."
    );
  }
  const r = await edgeFetch("/v1/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tier: "team",
      customer_email: opts.customerEmail || undefined,
      client_reference_id: opts.clientReferenceId || undefined,
      clerk_org_id: opts.clerkOrgId || undefined,
      success_url: `${commandAppUrl("/")}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${MARKETING_ORIGIN}/#pricing`,
    }),
  });
  const data = (await r.json()) as {
    url?: string;
    error?: string;
    hint?: string;
  };
  if (!r.ok || !data.url) {
    throw new Error(data.error || data.hint || "checkout_failed");
  }
  return { url: data.url };
}

/** After Checkout redirect — apply plan to user + org. */
export async function confirmCheckoutSession(opts: {
  sessionId: string;
  clerkUserId: string;
  clerkOrgId?: string | null;
}): Promise<{ ok: boolean; plan?: string; error?: string }> {
  const r = await edgeFetch("/v1/billing/confirm-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: opts.sessionId,
      clerk_user_id: opts.clerkUserId,
      clerk_org_id: opts.clerkOrgId || undefined,
    }),
  });
  const data = (await r.json()) as {
    ok?: boolean;
    plan?: string;
    error?: string;
    clerk_status?: number;
    clerk_body?: unknown;
  };
  if (!r.ok || !data.ok) {
    const detail =
      data.error ||
      (data.clerk_body
        ? `clerk_${data.clerk_status}: ${JSON.stringify(data.clerk_body).slice(0, 180)}`
        : "confirm_failed");
    throw new Error(detail);
  }
  return { ok: true, plan: data.plan || "hosted" };
}

export function planFromMetadata(
  meta: Record<string, unknown> | undefined | null
): PlanTier {
  const p = String(meta?.plan || "starter").toLowerCase();
  if (p === "team" || p === "hosted" || p === "scale") return p as PlanTier;
  return "starter";
}

export function isPaidPlan(plan: PlanTier): boolean {
  return plan === "team" || plan === "hosted" || plan === "scale";
}

/** Paid if user plan OR org plan is hosted. */
export function isPaidUserOrOrg(
  userMeta?: Record<string, unknown> | null,
  orgMeta?: Record<string, unknown> | null
): boolean {
  return (
    isPaidPlan(planFromMetadata(userMeta)) ||
    isPaidPlan(planFromMetadata(orgMeta))
  );
}

/**
 * Clerk Billing (B2B) entitlement via useAuth().has — OR legacy metadata.
 * Plan slugs: og_hosted | hosted | team | scale. Feature: hub_access.
 *
 * Accepts `unknown` because Clerk's `has` type is a strict exclusive union
 * (CheckAuthorizationWithCustomPermissions | …) that is not assignable to a
 * simple `( { plan?, feature? } ) => boolean` under TS function variance.
 */
export function isClerkBillingPaid(has: unknown): boolean {
  if (typeof has !== "function") return false;
  const check = has as (params: {
    plan?: string;
    feature?: string;
  }) => boolean;
  try {
    if (check({ feature: "hub_access" })) return true;
    if (check({ plan: "og_hosted" })) return true;
    if (check({ plan: "hosted" })) return true;
    if (check({ plan: "team" })) return true;
    if (check({ plan: "scale" })) return true;
  } catch {
    /* Billing not enabled on instance — has() may throw or no-op */
  }
  return false;
}

/** Prefer Clerk OrganizationProfile billing; set false to force Stripe portal. */
export function preferClerkBillingUi(): boolean {
  const v = (import.meta.env.VITE_CLERK_BILLING as string | undefined) || "";
  // Default true once Billing ships; env "0" forces Stripe portal path
  if (v === "0" || v === "false") return false;
  return true;
}

/**
 * After Clerk Billing subscribe (has() true), stamp public_metadata.plan for edge hub-access.
 */
export async function syncClerkEntitlement(opts: {
  sessionId: string;
  clerkOrgId?: string | null;
  plan?: string;
}): Promise<{ ok: boolean; plan?: string; error?: string }> {
  const r = await edgeFetch("/v1/billing/sync-entitlement", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: opts.sessionId,
      clerk_org_id: opts.clerkOrgId || undefined,
      plan: opts.plan || "hosted",
    }),
  });
  const data = (await r.json()) as {
    ok?: boolean;
    plan?: string;
    error?: string;
  };
  if (!r.ok || !data.ok) {
    return { ok: false, error: data.error || "sync_entitlement_failed" };
  }
  return { ok: true, plan: data.plan };
}

/** Provision branded hub hostname for org slug (edge → CF). Requires Clerk session. */
export async function ensureOrgDomain(
  slug: string,
  sessionId?: string | null
): Promise<{
  ok: boolean;
  hostname?: string;
  detail?: string;
  error?: string;
}> {
  const clean = slug
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 48);
  if (!clean) return { ok: false, detail: "invalid_slug" };
  if (!sessionId) {
    return { ok: false, detail: "session_required" };
  }
  try {
    const r = await edgeFetch("/v1/orgs/ensure-domain", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: clean, session_id: sessionId }),
    });
    return (await r.json()) as {
      ok: boolean;
      hostname?: string;
      detail?: string;
      error?: string;
    };
  } catch (e) {
    return {
      ok: false,
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}

/** Open Stripe Customer Portal (cancel / payment method / invoices). */
export async function openBillingPortal(opts: {
  sessionId: string;
  clerkOrgId?: string | null;
  returnUrl?: string;
}): Promise<{ url: string }> {
  const r = await edgeFetch("/v1/billing/portal", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: opts.sessionId,
      clerk_org_id: opts.clerkOrgId || undefined,
      return_url: opts.returnUrl || `${commandAppUrl("/")}`,
    }),
  });
  const data = (await r.json()) as {
    ok?: boolean;
    url?: string;
    error?: string;
    hint?: string;
    customer_id?: string;
    org_id?: string | null;
  };
  if (!r.ok || !data.url) {
    const parts = [data.error, data.hint].filter(Boolean);
    if (data.org_id) parts.push(`(org ${data.org_id})`);
    throw new Error(parts.join(" — ") || "billing_portal_failed");
  }
  return { url: data.url };
}

/** Hub browser access for paid Command users. */
export async function fetchHubAccess(opts: {
  sessionId: string;
  clerkUserId: string;
  orgId?: string | null;
  orgSlug?: string | null;
}): Promise<{
  hub_url: string;
  token: string;
  role: string;
  org_slug?: string;
  org_id?: string;
  domain_ready?: boolean;
  domain_detail?: string;
  hub_connected?: boolean;
}> {
  const r = await edgeFetch("/v1/command/hub-access", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: opts.sessionId,
      clerk_user_id: opts.clerkUserId,
      org_id: opts.orgId || undefined,
      org_slug: opts.orgSlug || undefined,
    }),
  });
  const data = (await r.json()) as {
    ok?: boolean;
    hub_url?: string;
    token?: string;
    role?: string;
    org_slug?: string;
    org_id?: string;
    domain_ready?: boolean;
    domain_detail?: string;
    hub_connected?: boolean;
    error?: string;
    hint?: string;
    detail?: string;
  };
  if (!r.ok || !data.token || !data.hub_url) {
    const code = data.error || "hub_access_failed";
    const detail = data.detail ? `: ${data.detail}` : "";
    const hint = data.hint ? ` — ${data.hint}` : "";
    throw new Error(`${code} (HTTP ${r.status})${detail}${hint}`);
  }
  return {
    hub_url: data.hub_url,
    token: data.token,
    role: data.role || "admin",
    org_slug: data.org_slug,
    org_id: data.org_id,
    domain_ready: data.domain_ready,
    domain_detail: data.domain_detail,
    hub_connected: data.hub_connected,
  };
}
